import { ActionButton } from '@common/components/forms/controls';
import { YesNoConfirmModal } from '@common/components/modals';
import { useApp } from '@common/hooks';
import { ILicenseModel } from '@common/models';
import { ILicenseModelWithNewLicenseImage } from '@hospital/models';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { Button, Col, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { UserProfileLicenseAddUpdate } from './UserProfileLicenseAddUpdate';
import { UserStore } from './UserStore';

const readonlyLicenseMessage = "Licenses and Registrations that originate from VetPoint can not be managed within PetPass.";

interface IUserProfileLicenses {
    store: UserStore;
    readOnly?: boolean;
}

export const UserProfileLicenses = observer(({ store, readOnly }: IUserProfileLicenses) => {
    const { configuration } = useApp();
    const [showUpdateLicenseModal, setShowUpdateLicenseModal] = useState<boolean>(false);
    const [updateLicense, setUpdateLicense] = useState<ILicenseModelWithNewLicenseImage | undefined>(undefined);

    const submitUpdateLicense = useCallback(async (license: ILicenseModelWithNewLicenseImage) => {
        await store.updateLicense(license, license.newLicensePhotoFile);
    }, [store]);

    const viewLicense = useCallback((license: ILicenseModel) => {
        if (license.licensePhoto && license.licensePhoto.photoUrl)
            window.open(license.licensePhoto.photoUrl, '_self');
    }, []);


    return (
        <>
            <YesNoConfirmModal
                visible={store.licenseToDelete != null}
                title="Confirm license deletion"
                description={store.licenseDeleteDescription}
                busy={store.licenseDeletePending}
                onConfirm={() => store.deleteLicense()}
                onCancel={() => store.updateLicenseToDelete(undefined)}
            />
            <Row className="pt-3">
                {!store.hasLicenses &&
                    <p><em>This user has no licenses or registrations.</em></p>
                }
                {store.hasLicenses &&
                    <Table hover className="table-responsive table-sm">
                        <thead>
                            <tr key={"header"}>
                                <th>Type</th>
                                <th>State</th>
                                <th>Number</th>
                                <th>Expiration</th>
                                <th>Status</th>
                                <th className="action">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {store.sortedLicenses.map((l) =>
                                <tr key={l.licenseId}>
                                    <td>{l.licenseType.name}</td>
                                    <td>{l.state.abbreviation}</td>
                                    <td>{l.number}</td>
                                    <td>{DateTime.fromISO(l.expirationDate).toLocaleString()}</td>
                                    <td>{(DateTime.fromISO(l.expirationDate) < DateTime.now()) ? <span className="expired">Expired</span> : "Active"}</td>
                                    <td className="action">
                                        {(l.isReadOnly && !readOnly) &&
                                            <OverlayTrigger overlay={<Tooltip id="tooltip-update-disabled">{readonlyLicenseMessage}</Tooltip>}>
                                                <span className="d-inline-block">
                                                    <ActionButton disabled iconclass="petco-update" text="Update" className="me-3" aria-label={`update ${l.licenseType.name} license`} style={{ pointerEvents: 'none' }} />
                                                </span>
                                            </OverlayTrigger>
                                        }
                                        {(!l.isReadOnly && !readOnly) && store.isActive &&
                                            <ActionButton iconclass="petco-update" text="Update" className="me-3" aria-label={`update ${l.licenseType.name} license`} onClick={() => {
                                                setUpdateLicense(l as ILicenseModelWithNewLicenseImage);
                                                setShowUpdateLicenseModal(true);
                                            }} />
                                        }
                                        {
                                            l.licensePhoto?.fileName &&
                                            <ActionButton iconclass="petco-view" disabled={l.licensePhoto?.photoUrl === undefined} text="View" className="me-3" aria-label={`view ${l.licenseType.name} license`} onClick={() => viewLicense(l)} />
                                        }
                                        {(l.isReadOnly && !readOnly) &&
                                            <OverlayTrigger overlay={<Tooltip id="tooltip-update-disabled">{readonlyLicenseMessage}</Tooltip>}>
                                                <span className="d-inline-block">
                                                    <ActionButton iconclass="petco-trash" disabled={l.isReadOnly} text="Delete" aria-label={`delete ${l.licenseType.name} license`} style={{ pointerEvents: 'none' }} />
                                                </span>
                                            </OverlayTrigger>
                                        }
                                        {(!l.isReadOnly && !readOnly) && store.isActive &&
                                            <ActionButton iconclass="petco-trash" disabled={l.isReadOnly} text="Delete" aria-label={`delete ${l.licenseType.name} license`} onClick={() => store.updateLicenseToDelete(l)} />
                                        }
                                    </td>
                                </tr>)
                            }
                        </tbody>
                    </Table>
                }
            </Row>
            {!readOnly && store.isActive &&
                <Row className="pt-3">
                    <Col xs={6}>
                        <Button
                            aria-label="add new license"
                            variant="outline-primary"
                            onClick={() => store.showAddLicenseModal(true)}>Add New License</Button>
                    </Col>
                </Row>
            }
            <UserProfileLicenseAddUpdate
                show={store.licenseAddModalVisible}
                store={store}
                configuration={configuration}
                onSubmit={(data: ILicenseModelWithNewLicenseImage) => store.addLicense(data, data.newLicensePhotoFile)}
                onCancel={() => store.showAddLicenseModal(false)}
            />
            <UserProfileLicenseAddUpdate
                show={showUpdateLicenseModal}
                store={store}
                configuration={configuration}
                existingLicense={updateLicense}
                onSubmit={submitUpdateLicense}
                onCancel={() => setShowUpdateLicenseModal(false)}
            />
        </>
    )
})
